import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Download, X } from "lucide-react";
import { API_BASE } from "@/lib/api";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "mfb_apk_seen_version";

export default function ReleaseNotesPopup() {
  const [info, setInfo] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/version/latest");
        if (!data?.available) return;
        const seen = localStorage.getItem(STORAGE_KEY);
        if (seen !== data.version) {
          setInfo(data);
          setOpen(true);
        }
      } catch {
        /* silent */
      }
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    if (info) localStorage.setItem(STORAGE_KEY, info.version);
    setOpen(false);
  };

  const download = () => {
    if (info) localStorage.setItem(STORAGE_KEY, info.version);
    trackEvent("download_app_click", { source: "release_notes_popup", version: info?.version });
    window.location.href = `${API_BASE}/download/latest`;
  };

  return (
    <AnimatePresence>
      {open && info && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={dismiss}
          data-testid="release-notes-popup"
        >
          <motion.div
            initial={{ y: 24, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 24, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md glass-strong border border-white/10 rounded-2xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.22),transparent_55%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.22),transparent_55%)] pointer-events-none" />
            <button
              onClick={dismiss}
              data-testid="release-notes-close"
              aria-label="Close"
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative p-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">New Version Available</span>
              </div>
              <h3 className="mt-4 font-[Outfit] text-3xl font-light tracking-tight text-white">
                MFB v{info.version}
              </h3>
              <div className="text-xs text-slate-500 mt-1">
                Build #{info.build_number} · {new Date(info.release_date).toLocaleDateString()}
              </div>

              {info.release_notes && (
                <div className="mt-5 rounded-xl border border-white/5 bg-white/5 p-4">
                  <div className="text-[10px] uppercase tracking-widest text-sky-400 mb-2">Release notes</div>
                  <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line">{info.release_notes}</p>
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={download}
                  data-testid="release-notes-download"
                  className="btn-gold flex-1"
                >
                  <Download className="w-4 h-4" /> Download now
                </button>
                <button onClick={dismiss} data-testid="release-notes-later" className="btn-ghost flex-1">
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
